/**
 * The seeker tone: the growl, and the moment it goes solid.
 *
 * ```
 *   saw ─► lowpass(f ← signal) ─┬─► AM gain ─► level ─► out
 *   LFO(square, f ← signal) ────┘   (depth → 0 at lock)
 * ```
 *
 * ## Why this sound is an instrument and not an alert
 *
 * A pilot flying an IR missile finds the target *by ear*. The seeker head's output is wired
 * straight into the headset, and what it produces is a rough, wandering growl whose pitch tracks
 * how much infra-red energy the head is seeing. Sweep the nose across the sky and the growl rises
 * and falls; settle on something hot and it climbs and steadies; hold it and the roughness
 * collapses into a clean, high, unmistakable note. Nobody has to be told what that transition
 * means, and nothing on a screen conveys it as fast.
 *
 * So the whole design is: **one continuous sound, two ends of a spectrum**, not two sounds with a
 * switch. Two parameters do all of it —
 *
 *   - **pitch**, from a low growl to a high tone as the signal strengthens;
 *   - **roughness**, which is amplitude modulation depth, and which goes to *zero* at lock.
 *
 * That second one is the important one. The "growl" is not a special waveform: it is this exact
 * tone with a heavy 50 Hz tremolo on it. Take the tremolo away and it is a clean tone. That is
 * why the lock reads as a *release* rather than as a new noise — the modulation stops, and what
 * is left is what was there all along.
 *
 * ## Why a sawtooth and not a sine
 *
 * A sine through a tremolo sounds like a doorbell. The real tone has harmonics — it is closer to
 * a buzz than a whistle — and a sawtooth through a tracking lowpass gives that, with the filter
 * opening as the pitch rises so the top of the range is bright without the bottom being harsh.
 *
 * `updateFrame(alpha)`: yes — {@link SeekerVoice.updateFrame}, once per rendered frame. Writes
 * `AudioParam` targets only; allocates nothing.
 */

import { setTarget } from '../param.js';
import { disconnectAll, gainNode, lowpass } from './parts.js';

/** Growl pitch at the faintest usable signal [Hz]. */
const GROWL_HZ = 165;
/** The clean tone at full lock [Hz]. */
const LOCK_HZ = 880;
/** Tremolo rate at the bottom of the range [Hz]. */
const RUMBLE_HZ = 26;
/** Tremolo rate just before lock [Hz] — it speeds up as the signal builds, which is the "urgency". */
const CHATTER_HZ = 95;
/** How quickly the tone follows the seeker [s]. Fast, but not instant: a real head has inertia. */
const GLIDE_S = 0.06;

export class SeekerVoice {
  private readonly context: AudioContext;

  private readonly carrier: OscillatorNode;
  private readonly tone: BiquadFilterNode;
  private readonly amplitude: GainNode;
  private readonly modulator: OscillatorNode;
  private readonly modulationDepth: GainNode;
  private readonly output: GainNode;

  constructor(context: AudioContext, destination: AudioNode) {
    this.context = context;

    this.carrier = context.createOscillator();
    this.carrier.type = 'sawtooth';
    this.carrier.frequency.value = GROWL_HZ;

    this.tone = lowpass(context, 900, 1.4);

    /*
     * The amplitude-modulation stage.
     *
     * `amplitude.gain` sits at a *base* of 0.5 and the modulator swings it either side. At full
     * depth (0.5) the gain reaches zero once per LFO cycle, which is what makes the growl chop;
     * at zero depth it sits at a steady 0.5 and the tone is continuous. One node, both behaviours.
     */
    this.amplitude = gainNode(context, 0.5);

    this.modulator = context.createOscillator();
    // Square, not sine: the real growl is a chop rather than a wobble.
    this.modulator.type = 'square';
    this.modulator.frequency.value = RUMBLE_HZ;
    this.modulationDepth = gainNode(context, 0.5);

    this.output = gainNode(context, 0);

    this.carrier.connect(this.tone);
    this.tone.connect(this.amplitude);
    this.modulator.connect(this.modulationDepth);
    // Driving the *gain param* rather than the signal path is what makes this AM rather than a mix.
    this.modulationDepth.connect(this.amplitude.gain);
    this.amplitude.connect(this.output);
    this.output.connect(destination);

    this.carrier.start();
    this.modulator.start();
  }

  /**
   * @param toneStrength 0…1 from the seeker — how much signal the head is seeing.
   * @param locked       True once the track has gone solid.
   * @param volume       Master weapons-bus contribution, 0…1. Zero silences it entirely.
   */
  updateFrame(toneStrength: number, locked: boolean, volume = 1): void {
    const now = this.context.currentTime;
    const signal = Math.max(0, Math.min(1, toneStrength));

    /*
     * Pitch, on a curve rather than linearly.
     *
     * Squaring the signal keeps the bottom of the range compressed, so the growl stays low and
     * menacing across most of the sweep and then climbs quickly near the top. A linear map spends
     * half its range on pitches that all sound like "nearly", which is the least useful thing for
     * the sound to say.
     */
    const pitch = GROWL_HZ + (LOCK_HZ - GROWL_HZ) * (locked ? 1 : signal * signal);
    setTarget(this.carrier.frequency, pitch, GLIDE_S, now);
    // The filter opens with the pitch, so the tone brightens as it acquires.
    setTarget(this.tone.frequency, pitch * 2.6 + 300, GLIDE_S, now);

    // Roughness: fast chatter as it builds, and gone the instant it locks.
    setTarget(
      this.modulator.frequency,
      RUMBLE_HZ + (CHATTER_HZ - RUMBLE_HZ) * signal,
      GLIDE_S,
      now,
    );
    setTarget(this.modulationDepth.gain, locked ? 0 : 0.5, locked ? 0.05 : GLIDE_S, now);

    /*
     * Level. A locked tone is deliberately a little louder than the loudest growl — the lock is
     * the thing the pilot is waiting for, and it should arrive rather than merely continue.
     */
    const level = signal <= 0.001 ? 0 : (locked ? 0.32 : 0.05 + 0.2 * signal) * volume;
    setTarget(this.output.gain, level, 0.05, now);
  }

  /** Silence it immediately — safed, or the mode ended. */
  silence(): void {
    setTarget(this.output.gain, 0, 0.02, this.context.currentTime);
  }

  dispose(): void {
    try {
      this.carrier.stop();
      this.modulator.stop();
    } catch {
      // Already stopped; nothing to do.
    }
    disconnectAll([
      this.carrier,
      this.tone,
      this.amplitude,
      this.modulator,
      this.modulationDepth,
      this.output,
    ]);
  }
}
